import { useContext, useEffect, useMemo, useState } from 'react';
import { MetricRecordFilters } from '@esg/syntiro-api-core';
import { MetricRecord, ReportingPeriodGroup } from '@esg/esg-global-types';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { MetadataError } from '@ep/error-handling';
import {
  MetricRecordEntitiesMap,
  getMetricRecords
} from '../../../graphql/services/metric_record.service';
import { values } from '../../../util/general_utils';
import { MetricExtended, metric_label } from '../../../lib/metric_capture/metric';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import PrimarySkeleton from '../../shared/loading/PrimarySkeleton';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import {
  FUNCTION_UNKNOWN_FAILURE,
  COMPONENT_UNKNOWN_FAILURE,
  FEEDBACK_ACTION_FAILURE,
  FEEDBACK_UNKNOWN_FAILURE
} from '../../../util/global_variables';
import DataGridToolbar from '../../shared/datagrid/DataGridToolbar';
import { GET_METRIC_RECORDS_QUALITATIVE } from '../../../graphql/queries/metric_record.queries';

interface QualitativeDataCompletenessDatagridProps {
  reporting_period_group: ReportingPeriodGroup;
  entities_map: MetricRecordEntitiesMap;
}

interface MetricGroupDataCompleteness {
  metric_group_name: string;
  total_metrics: number;
  responses: number;
}

interface QualitativeDataCompletenessGridRow {
  id: string;
  category_name: string;
  category_responses: string;
  completeness: number;
}

const SOURCE = 'QualitativeDataCompletenessDatagrid.tsx';

/**
 * Datagrid displaying the captured metric record progress for qualitative metrics.
 * @param {ReportingPeriodGroup} reporting_period_group Reporting period group to fetch metric record data for.
 * @param {MetricRecordEntitiesMap} entities_map Metric record reference entity data to use for datagrid.
 * @returns {JSX.Element}
 */
const QualitativeDataCompletenessDatagrid = ({
  reporting_period_group,
  entities_map
}: QualitativeDataCompletenessDatagridProps) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const [qualitativeMetricRecords, setQualitativeMetricRecords] = useState<Array<MetricRecord>>([]);
  const [metricRecordsLoading, setMetricRecordsLoading] = useState<boolean>(true);

  // Transform qualitative metrics and metric records into completeness datagrid rows.
  const data_completeness_rows: Array<QualitativeDataCompletenessGridRow> = useMemo(() => {
    // Create initialised lookup map for metric group data completeness by metric group ID.
    const metric_group_data_completeness_map = new Map<string, MetricGroupDataCompleteness>();
    const qualitative_metrics: Array<MetricExtended> = values<MetricExtended>(entities_map.metrics)
      .filter(
        (metric: MetricExtended) =>
          'is_quantitative' in metric.standard && !metric.standard.is_quantitative
      )
      .sort((a: MetricExtended, b: MetricExtended) => b.name.localeCompare(a.name));
    qualitative_metrics.forEach((metric: MetricExtended) => {
      const existing_data_completeness: MetricGroupDataCompleteness | undefined =
        metric_group_data_completeness_map.get(metric.metric_group.id);
      metric_group_data_completeness_map.set(metric.metric_group.id, {
        metric_group_name: metric.metric_group.name,
        total_metrics: (existing_data_completeness?.total_metrics ?? 0) + 1,
        responses: 0
      });
    });

    // Increase the response count of each respective metric group per fetched metric record.
    qualitativeMetricRecords.forEach((metric_record: MetricRecord) => {
      const metric_record_metric_group_id: string | undefined =
        entities_map.metrics[metric_record.metric.id]?.metric_group.id;
      const metric_group_data_completeness: MetricGroupDataCompleteness | undefined =
        metric_group_data_completeness_map.get(metric_record_metric_group_id);
      if (metric_group_data_completeness) {
        metric_group_data_completeness_map.set(metric_record_metric_group_id, {
          ...metric_group_data_completeness,
          responses: metric_group_data_completeness.responses + 1
        });
      }
    });

    // Transform the data completeness map into datagrid rows.
    const rows: Array<QualitativeDataCompletenessGridRow> = [];
    metric_group_data_completeness_map.forEach(
      (metric_group_data_completeness: MetricGroupDataCompleteness, metric_group_id: string) => {
        const metric_count: number = metric_group_data_completeness.total_metrics;
        const response_count: number = metric_group_data_completeness.responses;
        rows.push({
          id: metric_group_id,
          category_name: metric_group_data_completeness.metric_group_name,
          category_responses: `${response_count}/${metric_count}`,
          completeness: Math.round((response_count / metric_count) * 100)
        });
      }
    );
    return rows;
  }, [reporting_period_group, qualitativeMetricRecords]);

  // Fetch qualitative metric record data for given reporting period group.
  const fetchMetricRecords = async (): Promise<void> => {
    try {
      setMetricRecordsLoading(true);
      if (!group || !company || !reporting_period_group) {
        throw new Error('Cannot fetch Metric Records without required parameters');
      }
      const metric_record_filters: MetricRecordFilters = {
        reporting_period_group_id: reporting_period_group.id
      };
      const qualitative_metric_records: Array<MetricRecord> = await getMetricRecords(
        { group_id: group.id, company_id: company.id },
        metric_record_filters,
        GET_METRIC_RECORDS_QUALITATIVE
      );
      setQualitativeMetricRecords(qualitative_metric_records);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'fetchMetricRecords'),
          {
            group: group,
            company: company,
            reporting_period_group: reporting_period_group
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to fetch metric records', tracking_id),
        state: true,
        type: 'error'
      });
    } finally {
      setMetricRecordsLoading(false);
    }
  };

  useEffect(() => {
    try {
      fetchMetricRecords();
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : COMPONENT_UNKNOWN_FAILURE('QualitativeDataCompletenessDatagrid'),
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_UNKNOWN_FAILURE(tracking_id),
        state: true,
        type: 'error'
      });
    }
  }, [reporting_period_group]);

  const columns: Array<GridColDef> = [
    {
      field: 'category_name',
      headerName: 'Category',
      flex: 1
    },
    {
      field: 'category_responses',
      headerName: 'Responses',
      flex: 0.3
    },
    {
      field: 'completeness',
      headerName: 'Completeness (%)',
      renderCell: (params: GridRenderCellParams) => {
        return `${params.value}%`;
      },
      flex: 0.2
    }
  ];

  return metricRecordsLoading ? (
    <PrimarySkeleton variant="rounded" height={500} />
  ) : (
    <DataGrid
      autoHeight
      initialState={{
        pagination: {
          paginationModel: { pageSize: 10, page: 0 }
        },
        sorting: {
          sortModel: [{ field: 'completeness', sort: 'asc' }]
        }
      }}
      hideFooter={data_completeness_rows.length > 1 ? false : true}
      pageSizeOptions={[10, 25, 50, 100]}
      loading={metricRecordsLoading}
      rows={data_completeness_rows}
      columns={columns}
      disableRowSelectionOnClick
      slots={{
        toolbar: () => <DataGridToolbar entity_label={metric_label} />,
        noRowsOverlay: () => <DataGridNoRows missing_label={metric_label} />
      }}
      disableColumnSelector={true}
      sx={{ '&, [class^=MuiDataGrid]': { border: 'none' }, width: '100%' }}
    />
  );
};

export default QualitativeDataCompletenessDatagrid;
